import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  approveDomain, listApprovals, rejectDomain, getCategoriesIndex, listCategories,
} from '@src/services/Admin';
import {
  Button, Form, Modal, Select, Table, Input, message, Tooltip, TreeSelect, Switch, InputNumber,
} from 'antd';
import { dateFromMongoId } from '@src/utils/mongo-id';
import moment from 'moment';

const DR_TIER_THRESHOLD = 15;

function getBasePoints(dr) {
  return dr < DR_TIER_THRESHOLD ? DR_TIER_THRESHOLD : dr;
}

function formatCategories(categories) {
  return categories.map((category) => ({
    value: category._id,
    title: category.name,
    selectable: category.subCategories.length === 0,
    children: formatCategories(category.subCategories),
  }));
}

function displayCategory(categoryId, categoriesIndex) {
  if (!categoriesIndex) return '';
  let str = categoriesIndex[categoryId].name;
  let category = categoriesIndex[categoryId];
  while (category.parentId) {
    str = `${categoriesIndex[category.parentId].name} > ${str}`;
    category = categoriesIndex[category.parentId];
  }
  return str;
}

function Workspaces() {
  // Data retrieving
  const {
    isLoading,
    data: approvals,
    refetch: refetchList,
  } = useQuery(
    ['listApprovals'],
    () => listApprovals(),
  );
  const { data: categoriesIndex } = useQuery(
    ['getCategoriesIndex'],
    () => getCategoriesIndex(),
  );
  const { data: categories } = useQuery(
    ['listCategories'],
    () => listCategories(),
  );
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [reject, setReject] = useState(null);
  const [accept, setAccept] = useState(null);
  const onReject = async (values) => {
    try {
      setIsPerformingAction(true);
      await rejectDomain(reject._id, values.reason, values.reasonText);
      message.success('Success');
      setReject(null);
      refetchList();
    } catch (error) {
      message.error('Unexpected error while performing action!');
    }
    setIsPerformingAction(false);
  };
  const onApprove = async (values) => {
    try {
      setIsPerformingAction(true);
      await approveDomain(accept._id, values);
      message.success('Success');
      setAccept(null);
      refetchList();
    } catch (error) {
      message.error('Unexpected error while performing action!');
    }
    setIsPerformingAction(false);
  };
  const [reason, setReason] = useState(null);
  return (
    <div id="Approvals">
      <Table
        dataSource={approvals}
        loading={isLoading}
        pagination={false}
        rowKey="_id"
        size="small"
      >
        <Table.Column
          title={`Domain (${approvals?.length})`}
          key="_id"
          render={(_, d) => (
            <div>
              {d.alreadyOwnedBy.length > 0 && (
                <b>
                  ℹ️ Already owned by
                  {' '}
                  {d.alreadyOwnedBy.join(' ')}
                </b>
              )}
              <div>
                <a href={`https://${d.name}`} target="_blank" rel="noreferrer">{d.name}</a>
                {' • '}
                <a href={`https://ahrefs.com/website-authority-checker/?input=${d.name}`} target="_blank" rel="noreferrer">DR</a>
                {`: ${d.metrics ? d.metrics.authority.dr : '-'} • Lang: ${d.language}`}
                {d.aiClassification && d.aiClassification.language !== d.language && (
                  <span>
                    <i style={{ color: 'darkgrey' }}>{d.aiClassification.language}</i>
                    {' '}
                    🤖
                  </span>
                )}
                <Tooltip
                  title={d.logs.length ? (
                    <div>
                      {d.logs.map((l) => (
                        <div key={l._id}>
                          {`${moment(dateFromMongoId(l._id))
                            .format('YYYY-MM-DD @ HH:mm')} ${l.type}`}
                        </div>
                      ))}
                    </div>
                  ) : null}
                >
                  {` • Logs: ${d.logs.length}`}
                </Tooltip>
              </div>
              {d.status === 'TO_RE_REVIEW' && <div>✅ Previously Approved</div>}
              <div><i>{d.description}</i></div>
              {d.aiClassification && d.aiClassification.description !== d.description && (
                <div>
                  <i style={{ color: 'darkgrey' }}>{d.aiClassification.description}</i>
                  {' '}
                  🤖
                </div>
              )}
              <div>
                {d.categories.map((categoryId, i) => (
                  <div key={categoryId}>
                    <b>{`Category #${i + 1}: `}</b>
                    {displayCategory(categoryId, categoriesIndex)}
                  </div>
                ))}
              </div>
              {d.aiClassification
                && JSON.stringify(d.aiClassification.categories.sort())
                  !== JSON.stringify(d.categories.sort())
                && (
                  <div>
                    {d.aiClassification.categories.map((categoryId, i) => (
                      <div key={categoryId} style={{ color: 'darkgrey' }}>
                        <b>{`Category #${i + 1}: `}</b>
                        {displayCategory(categoryId, categoriesIndex)}
                        {' '}
                        🤖
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}
        />
        <Table.Column
          title="User"
          key="_id"
          render={(_, d) => (
            <div>
              <div>
                {`${d.user.email} • ${d.user.linkedinProfile.name} • ref=${d.user.ref}`}
                {d.user.linkedinProfile.url && (
                  <>
                    {' (⚠️ '}
                    <a href={d.user.linkedinProfile.url}>
                      scraped
                    </a>
                    )
                  </>
                )}
              </div>
              <div>
                {Object.entries(d.user.domainStats)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ')}
              </div>
            </div>
          )}
        />
        <Table.Column
          title="Actions"
          key="_id"
          render={(_, d) => (
            <div>
              <Button
                type="primary"
                size="small"
                disabled={!d.metrics}
                onClick={() => setAccept(d)}
              >
                {d.alreadyOwnedBy.length > 0 ? 'Merge' : 'Approve'}
              </Button>
              {' '}
              <Button
                type="danger"
                size="small"
                disabled={!d.metrics}
                onClick={() => setReject(d)}
              >
                Reject
              </Button>
            </div>
          )}
        />
      </Table>
      {reject && (
        <Modal
          visible
          title={`Confirm Reject ${reject.name}`}
          footer={null}
          closable={false}
        >
          <Form onFinish={onReject} disabled={isPerformingAction}>
            <Form.Item
              name="reason"
              rules={[{ required: true }]}
            >
              <Select onChange={(value) => setReason(value)}>
                <Select.Option value="LOWER_THAN_MIN_DR">LOWER_THAN_MIN_DR</Select.Option>
                <Select.Option value="DOMAIN_ALREADY_EXISTS">DOMAIN_ALREADY_EXISTS</Select.Option>
                <Select.Option value="FORBIDDEN_CATEGORY">FORBIDDEN_CATEGORY</Select.Option>
                <Select.Option value="OTHER">OTHER</Select.Option>
              </Select>
            </Form.Item>
            {['DOMAIN_ALREADY_EXISTS', 'OTHER'].includes(reason) && (
              <Form.Item
                name="reasonText"
                rules={[{ required: ['OTHER'].includes(reason) }]}
              >
                <Input />
              </Form.Item>
            )}
            <Form.Item noStyle>
              <div
                style={{
                  textAlign: 'right',
                  marginTop: 16,
                }}
              >
                <Button onClick={() => setReject(null)}>
                  CANCEL
                </Button>
                {' '}
                <Button htmlType="Submit" loading={isPerformingAction} type="primary">
                  CONFIRM
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {accept && (
        <Modal
          visible
          title={`Confirm Accept ${accept.name}`}
          footer={null}
          closable={false}
        >
          <Form
            onFinish={onApprove}
            disabled={isPerformingAction}
            layout="vertical"
            initialValues={{
              description: accept.description,
              language: accept.language,
              categories: accept.categories,
              isYC: false,
              collectedPoints: 0,
            }}
          >
            {accept.alreadyOwnedBy.length > 0 && (
              <div>
                <b>
                  ℹ️ Already owned by
                  {' '}
                  {accept.alreadyOwnedBy.join(' ')}
                </b>
                <br />
                <br />
              </div>
            )}
            <Form.Item
              name="language"
              label="Language"
              rules={[
                { required: true, max: 2, min: 2 },
              ]}
            >
              <Input disabled={accept.alreadyOwnedBy.length > 0} />
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
              rules={[
                { required: true, max: 100 },
              ]}
            >
              <Input disabled={accept.alreadyOwnedBy.length > 0} />
            </Form.Item>
            <Form.Item
              name="categories"
              label="Categories"
              rules={[{ required: true }]}
            >
              <TreeSelect
                showSearch
                multiple
                disabled={accept.alreadyOwnedBy.length > 0}
                treeData={formatCategories(categories.find((c) => c.name === 'Business Software').subCategories)}
                treeNodeFilterProp="children"
                filterTreeNode={(input, treeNode) => {
                  const { title } = treeNode.props;
                  const searchText = title.toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
              />
            </Form.Item>
            <Form.Item
              name="isYC"
              label="Is YC?"
              valuePropName="checked"
            >
              <Switch disabled={accept.alreadyOwnedBy.length > 0} />
            </Form.Item>
            <Form.Item
              name="collectedPoints"
              label="Points to collect"
              rules={[
                { required: true },
              ]}
              extra={(
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => prev.collectedPoints !== curr.collectedPoints}
                >
                  {({ getFieldValue }) => {
                    const collectedPoints = getFieldValue('collectedPoints') ?? 0;
                    const basePoints = getBasePoints(accept.metrics.authority.dr);
                    const domainPoints = basePoints - collectedPoints;
                    return `Domain gets ${domainPoints} points (${basePoints} − ${collectedPoints})`;
                  }}
                </Form.Item>
              )}
            >
              <InputNumber
                min={0}
                max={getBasePoints(accept.metrics.authority.dr)}
                disabled={accept.alreadyOwnedBy.length > 0}
              />
            </Form.Item>
            <Form.Item noStyle>
              <div
                style={{
                  textAlign: 'right',
                  marginTop: 16,
                }}
              >
                <Button onClick={() => setAccept(null)}>
                  CANCEL
                </Button>
                {' '}
                <Button htmlType="Submit" loading={isPerformingAction} type="primary">
                  {accept.alreadyOwnedBy.length > 0 ? 'MERGE' : 'CONFIRM'}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
}

Workspaces.propTypes = {};

export default Workspaces;
